import api from '@pray/shared/utils/api';

import type { TopContentApiItem, TopContentItem, TopContentPageResult } from './types';

interface GetTopContentParams {
  artistId: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  bustCache?: boolean;
  signal?: AbortSignal;
  timeout?: number;
}

// Paginated list envelope shape returned by the top-content endpoints.
interface TopContentApiEnvelope {
  data: TopContentApiItem[];
  total?: number;
}

function mapItem(item: TopContentApiItem): TopContentItem {
  return {
    rank: item.rank,
    contentId: item.content_id ?? null,
    contentTitle: item.content_title,
    seriesTitle: item.series_title,
    totalCount: item.total_count,
    totalDuration: item.total_duration,
    totalCompleted: item.total_completed,
    uniqueListeners: item.unique_listeners ?? null,
  };
}

function buildQueryString({ startDate, endDate, limit, offset, bustCache }: GetTopContentParams): string {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (limit != null) params.append('limit', String(limit));
  if (offset != null) params.append('offset', String(offset));
  if (bustCache) params.append('bustCache', 'true');

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

async function fetchTopContentPage(
  url: string,
  offset: number,
  { signal, timeout }: Pick<GetTopContentParams, 'signal' | 'timeout'>
): Promise<TopContentPageResult> {
  const response = await api.executeGetRequest<TopContentApiEnvelope>(url, {}, { signal, timeout });

  return {
    items: (response.data.data ?? []).map(mapItem),
    total: response.data.total ?? 0,
    offset,
  };
}

/**
 * Get a page of top listened content for an artist (paginated "Show more").
 */
export async function getListenTopContent(params: GetTopContentParams): Promise<TopContentPageResult> {
  if (!params.artistId) throw new Error('Artist ID is required');

  const offset = params.offset ?? 0;
  const url = `/web/studio/artists/${params.artistId}/analytics/listen/top-content${buildQueryString(params)}`;

  return fetchTopContentPage(url, offset, params);
}

/**
 * Get a page of top watched content for an artist (paginated "Show more").
 */
export async function getWatchTopContent(params: GetTopContentParams): Promise<TopContentPageResult> {
  if (!params.artistId) throw new Error('Artist ID is required');

  const offset = params.offset ?? 0;
  const url = `/web/studio/artists/${params.artistId}/analytics/watch/top-content${buildQueryString(params)}`;

  return fetchTopContentPage(url, offset, params);
}
